import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Anchor, Book, Calendar, Users, Sailboat, Wind, Shield, FileText } from 'lucide-react';

const RulesReference: React.FC = () => {
  const [activeTab, setActiveTab] = useState('roles');

  return (
    <div className="container mx-auto p-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-2xl">Seafaring Rules Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-4 mb-6">
              <TabsTrigger value="roles" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Ship Roles
              </TabsTrigger>
              <TabsTrigger value="actions" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Role Actions
              </TabsTrigger>
              <TabsTrigger value="seafaring" className="flex items-center gap-2">
                <Sailboat className="h-4 w-4" />
                Seafaring Turns
              </TabsTrigger>
              <TabsTrigger value="daily" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Daily Activities
              </TabsTrigger>
            </TabsList>

            {/* Ship Roles Tab */}
            <TabsContent value="roles">
              <div className="space-y-6">
                <section>
                  <h3 className="font-bold text-lg mb-3">Roles & Filling Rules</h3>
                  <div className="bg-muted p-4 rounded-md mb-4">
                    <ul className="list-disc pl-5 space-y-2 text-sm">
                      <li>A character can fill up to 2 roles.</li>
                      <li>A character cannot fill 2 core roles or 2 magic roles simultaneously.</li>
                      <li>Checks in these roles can be made using the primary or secondary skill.</li>
                      <li>Checks made with the secondary skill are made at a -5 penalty.</li>
                    </ul>
                  </div>
                  
                  <div className="bg-muted p-4 rounded-md mb-4">
                    <h4 className="font-bold mb-2">Role Vacancy Penalties</h4>
                    <ul className="list-disc pl-5 space-y-2 text-sm">
                      <li>A ship with no Captain or First Mate takes a -5 penalty on all seafaring checks.</li>
                      <li>A ship with no Pilot takes a -5 penalty on all navigation checks.</li>
                      <li>A ship takes a cumulative -2 penalty on all seafaring checks for all core roles that aren't filled.</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Core Roles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <RoleCard 
                      title="Captain"
                      primary="Profession (Sailor)"
                      secondary="Diplomacy, Intimidate"
                      isCore={true}
                    />
                    <RoleCard 
                      title="First Mate"
                      primary="Profession (Sailor)"
                      secondary="Diplomacy, Intimidate"
                      isCore={true}
                    />
                    <RoleCard 
                      title="Pilot"
                      primary="Knowledge (Geography)"
                      secondary="Survival"
                      isCore={true}
                    />
                  </div>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Magic Roles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <RoleCard 
                      title="Mage"
                      primary="Knowledge (Arcana)"
                      secondary="Spellcraft"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Prelate"
                      primary="Knowledge (Religion)"
                      secondary="Diplomacy"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Windsinger"
                      primary="Knowledge (Nature)"
                      secondary="Perform (any)"
                      isCore={false}
                    />
                  </div>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Secondary Roles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <RoleCard 
                      title="Artillerist"
                      primary="Profession (Siege Engineer)"
                      secondary="Knowledge (Engineering)"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Boatswain"
                      primary="Use Rope"
                      secondary="Profession (Sailor)"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Cook"
                      primary="Profession (Cook)"
                      secondary="Survival"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Conning Officer"
                      primary="Profession (Sailor)"
                      secondary="None"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Master-at-Arms"
                      primary="Intimidate"
                      secondary="Craft (Weaponsmith)"
                      isCore={false}
                    />
                    <RoleCard 
                      title="Surgeon"
                      primary="Heal"
                      secondary="Craft (Alchemy)"
                      isCore={false}
                    />
                  </div>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Crew Action Points (CAP)</h3>
                  <div className="bg-muted p-4 rounded-md">
                    <ul className="list-disc pl-5 space-y-2 text-sm">
                      <li>At the start of each Seafaring turn, every player gains 1 CAP for each role they fill.</li>
                      <li>CAPs can be spent at any point during the Seafaring turn to perform Role Actions.</li>
                      <li>Players lose all unspent CAPs at the end of each Seafaring turn.</li>
                      <li>CAPs can be granted or removed at DM's discretion.</li>
                      <li>NPCs fulfilling roles may or may not utilize CAPs at DM's discretion.</li>
                      <li>All checks are made against DC 10 unless otherwise stated. DC can be increased or decreased based on circumstance at DM's discretion.</li>
                      <li>All bonuses granted last until the end of the current seafaring turn.</li>
                    </ul>
                  </div>
                </section>
              </div>
            </TabsContent>

            {/* Role Actions Tab */}
            <TabsContent value="actions">
              <Accordion type="multiple" className="w-full">
                <AccordionItem value="core-roles">
                  <AccordionTrigger className="font-bold text-lg">Core Role Actions</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      <ActionCard
                        role="Captain"
                        name="Leadership"
                        cost={1}
                        description="Roll a Captain check to grant a +2 competence bonus on all of the crew's Profession(Sailor) checks. Grant an additional +1 for every 5 points you exceed the DC."
                        checkType="Captain check"
                      />
                      <ActionCard
                        role="First Mate"
                        name="Morale Boost"
                        cost={1}
                        description="Roll a First Mate check to grant a +1 morale bonus on all of the crew's skill checks. Grant an additional +1 for every 10 points you exceed the DC."
                        checkType="First Mate check"
                      />
                      <ActionCard
                        role="Pilot"
                        name="Dead Reckoning"
                        cost={1}
                        description="Roll a Pilot check to grant a +2 insight bonus on your next Pilot check. Grant an additional +1 for every 10 points you exceed the DC. This bonus can be used in the seafaring turn after this one, but is lost at the end of that turn."
                        checkType="Pilot check"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="magic-roles">
                  <AccordionTrigger className="font-bold text-lg">Magic Role Actions</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      <ActionCard
                        role="Mage"
                        name="Arcane Augmentation"
                        cost={1}
                        description="Roll a Mage check to grant a +1 enhancement bonus to the ship's hardness."
                        checkType="Mage check"
                      />
                      <ActionCard
                        role="Prelate"
                        name="Safe Harbor"
                        cost={1}
                        description="Roll a Prelate check to have one crew member you can see reroll a failed skill check."
                        checkType="Prelate check"
                      />
                      <ActionCard
                        role="Windsinger"
                        name="Shape the Winds"
                        cost={1}
                        description="Roll a Windsinger check to increase the base sailing speed of the vessel by 5ft (enhancement bonus). (½ mph)."
                        checkType="Windsinger check"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="secondary-roles">
                  <AccordionTrigger className="font-bold text-lg">Secondary Role Actions</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      <ActionCard
                        role="Artillerist"
                        name="Weapon Maintenance"
                        cost={1}
                        description="Roll an Artillerist check to gain a +1 competence bonus to attack rolls with ship weapons for the crew. Grant an additional +1 for every 10 points you exceed the DC."
                        checkType="Artillerist check"
                      />
                      <ActionCard
                        role="Boatswain"
                        name="Inspect Rigging"
                        cost={1}
                        description="Roll a Boatswain check to grant a +2 circumstance bonus on all of the crew's Balance and Climb checks while using ropes. Grant an additional +1 for every 5 points you exceed the DC."
                        checkType="Boatswain check"
                      />
                      <ActionCard
                        role="Cook"
                        name="Prepare Hearty Meal"
                        cost={1}
                        description="Roll a Cook check to grant +2 temporary HP to the crew. Grant an additional +1 for every 10 points you exceed the DC. (Cannot be used in combat)"
                        checkType="Cook check"
                      />
                      <ActionCard
                        role="Conning Officer"
                        name="Handle the Ship"
                        cost={1}
                        description="Roll a Conning Officer check to have the helmsman roll twice on their next ship handling check and take the higher result."
                        checkType="Conning Officer check"
                      />
                      <ActionCard
                        role="Master-at-Arms"
                        name="Combat Drills"
                        cost={1}
                        description="Roll a Master-at-Arms check to grant a +1 competence bonus on weapon attack rolls for the crew while they are on the deck of the ship. Grant an additional +1 for every 10 points you exceed the DC."
                        checkType="Master-at-Arms check"
                      />
                      <ActionCard
                        role="Surgeon"
                        name="Preventive Care"
                        cost={1}
                        description="Roll a Surgeon check to grant a +1 circumstance bonus on Constitution saves against poisons and diseases for the crew. Grant an additional +1 for every 10 points you exceed the DC."
                        checkType="Surgeon check"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            {/* Seafaring Turns Tab */}
            <TabsContent value="seafaring">
              <div className="space-y-6">
                <section>
                  <h3 className="font-bold text-lg mb-3">Danger Levels & Turn Duration</h3>
                  <div className="space-y-4">
                    <DangerLevelCard 
                      level="Low"
                      days={3}
                      description="Low danger waters are waters with not a lot of activity that have been charted and are known. Seafaring turns take place over 3 days in low danger waters."
                    />
                    <DangerLevelCard 
                      level="Medium"
                      days={2}
                      description="Medium danger waters are waters where there are active threats or hazards to the ship and crew. Seafaring turns take place over 2 days in medium danger waters."
                    />
                    <DangerLevelCard 
                      level="High"
                      days={1}
                      description="High danger waters are waters that are uncharted or otherwise highly hostile in environment or inhabitants. Seafaring turns take place every day in high danger waters."
                    />
                  </div>
                  <div className="bg-muted p-4 rounded-md mt-4">
                    <p className="text-sm">
                      Hexes are not necessarily static in their danger level. Circumstances such as inclement weather or being pursued by a ship or monster can elevate the danger levels of otherwise low or medium danger waters. This is largely up to DM discretion.
                    </p>
                  </div>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Seafaring Turn Sequence</h3>
                  <div className="bg-muted p-4 rounded-md mb-4">
                    <p className="text-sm mb-2">The following steps happen in order at the start of each seafaring turn:</p>
                    <ol className="list-decimal pl-5 space-y-2 text-sm">
                      <li>Determine Wind and Weather</li>
                      <li>Determine Navigation</li>
                      <li>Determine Morale</li>
                      <li>Determine Cleanliness</li>
                      <li>Check for Events or Encounters</li>
                    </ol>
                  </div>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Weather & Navigation</h3>
                  <Accordion type="multiple" className="w-full">
                    <AccordionItem value="weather">
                      <AccordionTrigger>Wind and Weather</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 text-sm">
                          <p>This happens as outlined in Stormwrack, with the addition of a weather prediction check.</p>
                          <ul className="list-disc pl-5 space-y-2">
                            <li>Weather prediction check: Survival check or Knowledge (Nature) at -5 penalty.</li>
                            <li>DC: 15 in clear precipitation, 20 in rain/snow, 25 in fog.</li>
                            <li>+2 bonus if made by a ship Windsinger or Prelate.</li>
                            <li>Success: No penalties.</li>
                            <li>Failure: -2 penalty on Profession (Sailor) checks in heavy winds/storms.</li>
                            <li>Failure by 5+: Roll twice on Profession (Sailor) checks and take the lower result.</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="navigation">
                      <AccordionTrigger>Navigation</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 text-sm">
                          <p>This happens as outlined in Stormwrack except it's a Pilot check instead of a strictly Knowledge (Geography) check.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </section>

                <section>
                  <h3 className="font-bold text-lg mb-3">Morale & Cleanliness</h3>
                  <Accordion type="multiple" className="w-full">
                    <AccordionItem value="morale">
                      <AccordionTrigger>Morale States & Checks</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="font-medium">Morale States:</h4>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li><strong>Heartened:</strong> Crew members gain a +1 morale bonus on Will saves against fear effects while on the ship.</li>
                              <li><strong>Normal:</strong> No bonuses or penalties.</li>
                              <li><strong>Discouraged:</strong> The rally check is made at a -2 penalty.</li>
                              <li><strong>Unnerved:</strong> The crew has the shaken condition while on the ship.</li>
                              <li><strong>Resentful:</strong> The crew has the shaken condition while on the ship and the captain rolls twice on all Profession (Sailor) and Captain checks and takes the lower result.</li>
                              <li><strong>Mutinous:</strong> The crew mutinies.</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <h4 className="font-medium">Morale Checks:</h4>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li>The Captain rolls a DC 15 Captain check to rally his crew at the start of each seafaring turn.</li>
                              <li>For every 5 points exceeding the DC, morale goes up 1 step.</li>
                              <li>For every 5 points below the DC, morale drops by 1 step.</li>
                              <li>If no Captain role is filled, morale automatically drops by 1 step.</li>
                              <li>Any player may spend a CAP on Carousing to make a DC 15 Diplomacy or Intimidate check to increase morale by 1 step.</li>
                            </ul>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="cleanliness">
                      <AccordionTrigger>Cleanliness States & Checks</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="font-medium">Cleanliness States:</h4>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li><strong>Pristine:</strong> +1 bonus to all Profession (Sailor) checks made by crew while on the ship.</li>
                              <li><strong>Standard:</strong> No bonuses or penalties.</li>
                              <li><strong>Neglected:</strong> -1 penalty to all Profession (Sailor) checks made by crew while on the ship. 10% chance of disease outbreak.</li>
                              <li><strong>Hazardous:</strong> -2 penalty to all Profession (Sailor) checks made by crew while on the ship. 50% chance of disease outbreak.</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <h4 className="font-medium">Cleanliness Checks:</h4>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li>The First Mate rolls a DC 15 First Mate check at the start of each turn.</li>
                              <li>For every 5 points exceeding the DC, cleanliness goes up 1 step.</li>
                              <li>For every 5 points below the DC, cleanliness drops by 1 step.</li>
                              <li>If no First Mate role is filled, cleanliness automatically drops by 1 step.</li>
                              <li>Any player may spend a CAP on Cleaning to make a DC 15 Profession (Sailor) or Search check to increase cleanliness by 1 step.</li>
                            </ul>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </section>
              </div>
            </TabsContent>

            {/* Daily Activities Tab */}
            <TabsContent value="daily">
              <div className="space-y-6">
                <section>
                  <h3 className="font-bold text-lg mb-3">Daily Activities</h3>
                  <div className="bg-muted p-4 rounded-md mb-4 text-sm">
                    <p>A player may spend time performing 1 daily activity per day within the seafaring turn. These activities must be declared at the start of the seafaring turn.</p>
                    <p className="mt-2">For example, the seafaring turn will last 3 days due to calm waters, so Player X chooses to Scout on day 1, Fish on day 2, and Gamble on day 3.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DailyActivityCard
                      name="Repairs"
                      description="Repair 5 HP to a damaged hull section or shore a weakened section."
                    />
                    <DailyActivityCard
                      name="Scouting"
                      description="Lower the chances of a hostile encounter by 5%."
                    />
                    <DailyActivityCard
                      name="Fishing/Foraging"
                      description="Take 20 on a Survival check for foraging food."
                    />
                    <DailyActivityCard
                      name="Carousing"
                      description="Attempt to increase Crew Morale (See: Determine Morale)"
                    />
                    <DailyActivityCard
                      name="Guard Duty"
                      description="Choose Morning, Afternoon, Evening, or Night. You are actively alert for potential dangers during this timeframe."
                    />
                    <DailyActivityCard
                      name="Cleaning"
                      description="Attempt to increase Cleanliness (See: Determine Cleanliness)"
                    />
                    <DailyActivityCard
                      name="Crafting"
                      description="Spend time crafting"
                    />
                    <DailyActivityCard
                      name="Training/Retraining"
                      description="Spend time training, researching, or retraining"
                    />
                    <DailyActivityCard
                      name="Gambling"
                      description="Gamble amongst the crew for a chance to win gold or items"
                    />
                    <DailyActivityCard
                      name="Storytelling"
                      description="Regale the crew with tales and legends to increase Fame or Infamy"
                    />
                  </div>
                </section>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// Component for displaying role information
interface RoleCardProps {
  title: string;
  primary: string;
  secondary: string;
  isCore: boolean;
}

const RoleCard: React.FC<RoleCardProps> = ({ title, primary, secondary, isCore }) => {
  return (
    <div className="border rounded-md p-4 bg-card">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-bold">{title}</h4>
        {isCore && <Badge className="bg-blue-500">Core</Badge>}
      </div>
      <div className="space-y-1 text-sm">
        <div>
          <span className="font-medium">Primary:</span> {primary}
        </div>
        <div>
          <span className="font-medium">Secondary:</span> {secondary}
        </div>
      </div>
    </div>
  );
};

// Component for displaying role actions
interface ActionCardProps {
  role: string;
  name: string;
  cost: number;
  description: string;
  checkType: string;
}

const ActionCard: React.FC<ActionCardProps> = ({ role, name, cost, description, checkType }) => {
  return (
    <div className="border rounded-md p-4 bg-card">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-bold">{role} – {name}</h4>
        <Badge>{cost} CAP</Badge>
      </div>
      <div className="text-sm">
        <p>{description}</p>
        <p className="mt-2 font-medium">Roll a {checkType}</p>
      </div>
    </div>
  );
};

// Component for displaying danger levels
interface DangerLevelCardProps {
  level: string;
  days: number;
  description: string;
}

const DangerLevelCard: React.FC<DangerLevelCardProps> = ({ level, days, description }) => {
  let bgColor = "bg-green-100 dark:bg-green-900";
  if (level === "Medium") bgColor = "bg-yellow-100 dark:bg-yellow-900";
  if (level === "High") bgColor = "bg-red-100 dark:bg-red-900";
  
  return (
    <div className={`rounded-md p-4 ${bgColor}`}>
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-bold">{level} Danger</h4>
        <Badge variant="outline">{days} {days === 1 ? 'day' : 'days'} per turn</Badge>
      </div>
      <p className="text-sm">{description}</p>
    </div>
  );
};

// Component for displaying daily activities
interface DailyActivityCardProps {
  name: string;
  description: string;
}

const DailyActivityCard: React.FC<DailyActivityCardProps> = ({ name, description }) => {
  return (
    <div className="border rounded-md p-4 bg-card">
      <h4 className="font-bold mb-2">{name}</h4>
      <p className="text-sm">{description}</p>
    </div>
  );
};

export default RulesReference;